import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { ApiError } from "../api/errors";
import { useGeoConsent, useGeoHide, useGeoMap, useGeoStatus } from "../api/hooks";
import type { GeoBoardEntry, GeoLocation, GeoMapView } from "../api/types";
import { GeoConsentSheet } from "../components/GeoConsentSheet";
import { WorldDotMap } from "../components/WorldDotMap";
import { Button, Card, Chip, ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { formatCompact } from "../lib/format";
import { countryName, flagEmoji } from "../lib/geo";
import { showBackButtonWith } from "../telegram/sdk";

// Geo map (design "Map consent" / "Map opted-in" artboards). One route, two
// states: until consent is granted the map renders blurred behind the opt-in
// sheet; afterwards the full map + boards. Reached from the board tab, so the
// /board NavLink stays highlighted (route is /board/map).

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(4)};
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const OnlineDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.color.satiety};
  box-shadow: 0 0 6px ${({ theme }) => theme.color.satiety};
`;

const MapCard = styled(Card)`
  background:
    radial-gradient(120% 100% at 50% 0%, rgba(0, 152, 234, 0.12), transparent 60%),
    ${({ theme }) => theme.color.surface};
`;

// --- consent state ---

const Dimmed = styled.div`
  filter: blur(6px) brightness(0.7);
  opacity: 0.65;
  pointer-events: none;
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(8, 9, 12, 0.35) 0%,
    rgba(8, 9, 12, 0.9) 55%,
    ${({ theme }) => theme.color.base} 100%
  );
`;

// Docked above the sticky TabBar (z-index 10), like the Toast.
const SheetDock = styled.div`
  position: fixed;
  bottom: calc(76px + env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 24px);
  max-width: calc(${({ theme }) => theme.maxWidth} - 24px);
  z-index: 5;
`;

// --- opted-in state ---

const YouCallout = styled.div`
  margin-top: ${({ theme }) => theme.space(3)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space(2)};
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.25);
`;

const CalloutLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CalloutDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.accent};
  box-shadow: 0 0 8px ${({ theme }) => theme.color.accent};
`;

const CalloutText = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    color: ${({ theme }) => theme.color.textDim};
  }
`;

const HideBtn = styled(Button)`
  flex-shrink: 0;
  padding: 4px 10px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.textDim};
  border-color: ${({ theme }) => theme.color.borderHi};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space(2)};
`;

const StatBox = styled.div`
  padding: 12px 10px;
  text-align: center;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.color.text};
`;

const StatLabel = styled.div`
  margin-top: 2px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 9.5px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.color.textDim};
`;

const Note = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

const BoardCard = styled(Card)`
  padding: 0;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 26px 24px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.color.border};
  }
`;

const RRank = styled.span`
  text-align: center;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.textDim};
`;

const RFlag = styled.span`
  font-size: 20px;
  line-height: 1;
  text-align: center;
`;

const CityDot = styled.span`
  justify-self: center;
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.accent};
  box-shadow: 0 0 8px ${({ theme }) => theme.color.accent};
`;

const RMain = styled.div`
  min-width: 0;
`;

const RName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RNameMeta = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

const RSub = styled.div`
  margin-top: 2px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textDim};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  b {
    color: ${({ theme }) => theme.color.accent};
    font-weight: 500;
  }
`;

const RRight = styled.div`
  text-align: right;
`;

const RCount = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
`;

const RUnit = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 9.5px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const Empty = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.color.textFaint};
  padding: ${({ theme }) => theme.space(4)};
  text-align: center;
`;

function displayError(e: Error): string {
  return e instanceof ApiError ? e.display : e.message;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <StatBox>
      <StatValue>{value}</StatValue>
      <StatLabel>{label}</StatLabel>
    </StatBox>
  );
}

function BoardRows({ entries, kind }: { entries: GeoBoardEntry[]; kind: "country" | "city" }) {
  if (entries.length === 0) return <Empty>no raccoons pinned yet — you're early</Empty>;
  return (
    <>
      {entries.map((e) => (
        <Row key={`${e.countryCode}-${e.city ?? ""}`}>
          <RRank>{String(e.rank).padStart(2, "0")}</RRank>
          {kind === "country" ? <RFlag>{flagEmoji(e.countryCode)}</RFlag> : <CityDot />}
          <RMain>
            <RName>
              {kind === "country" ? (
                countryName(e.countryCode)
              ) : (
                <>
                  {e.city} <RNameMeta>· {flagEmoji(e.countryCode)}</RNameMeta>
                </>
              )}
            </RName>
            {e.topUser && (
              <RSub>
                top: <b>{e.topUser.displayName}</b> · {e.topUser.points.toLocaleString()} pts
              </RSub>
            )}
          </RMain>
          <RRight>
            <RCount>{formatCompact(e.raccoons)}</RCount>
            <RUnit>raccoons</RUnit>
          </RRight>
        </Row>
      ))}
    </>
  );
}

/** Opted-in map. Exported presentational view (also used by the dev preview entry). */
export function MapView({
  map,
  pinned,
  onHide,
  hidePending,
}: {
  map: GeoMapView;
  /** Viewer's stored pin from GET /geo/status — the only source of "you" coordinates. */
  pinned: GeoLocation | null;
  onHide: () => void;
  hidePending: boolean;
}) {
  const you = pinned ?? map.you;
  const youCoords =
    pinned && pinned.lat !== null && pinned.lng !== null
      ? { lat: pinned.lat, lng: pinned.lng }
      : null;

  return (
    <Stack>
      <Header>
        <SectionLabel>// map · raccoons worldwide</SectionLabel>
        <Chip>
          <OnlineDot />
          {map.online.toLocaleString()} online
        </Chip>
      </Header>

      <MapCard ticks radius="xl">
        <WorldDotMap pins={map.pins} you={youCoords} />
        {you && (
          <YouCallout>
            <CalloutLeft>
              <CalloutDot />
              <CalloutText>
                you · {you.city ? `${you.city}, ${you.countryCode.toUpperCase()}` : countryName(you.countryCode)}{" "}
                <span>· from IP</span>
              </CalloutText>
            </CalloutLeft>
            <HideBtn type="button" $variant="ghost" disabled={hidePending} onClick={onHide}>
              {hidePending ? "hiding…" : "hide me"}
            </HideBtn>
          </YouCallout>
        )}
      </MapCard>

      <Stats>
        <Stat label="countries" value={formatCompact(map.stats.countries)} />
        <Stat label="cities" value={formatCompact(map.stats.cities)} />
        <Stat label="raccoons" value={formatCompact(map.stats.raccoons)} />
      </Stats>

      <div>
        <Header>
          <SectionLabel>// countries</SectionLabel>
          <Note>by raccoons</Note>
        </Header>
        <BoardCard>
          <BoardRows entries={map.countries} kind="country" />
        </BoardCard>
      </div>

      <div>
        <Header>
          <SectionLabel>// cities</SectionLabel>
          <Note>top 5 cities</Note>
        </Header>
        <BoardCard>
          <BoardRows entries={map.cities} kind="city" />
        </BoardCard>
      </div>
    </Stack>
  );
}

export function GeoMap() {
  const navigate = useNavigate();
  const status = useGeoStatus();
  const mapQuery = useGeoMap();
  const consent = useGeoConsent();
  const hide = useGeoHide();

  // Telegram BackButton returns to the board (same pattern as the riddle modal).
  useEffect(() => showBackButtonWith(() => navigate("/board")), [navigate]);

  if (status.isLoading) return <Loading label="locating raccoons…" />;
  if (status.error) return <ErrorNotice message={displayError(status.error)} />;
  if (!status.data) return null;

  if (status.data.status !== "granted") {
    // "none" and "hidden" both land here: the sheet doubles as the re-opt-in path.
    return (
      <Stack>
        <Header>
          <SectionLabel>// map · raccoons worldwide</SectionLabel>
        </Header>
        <Dimmed>
          <MapCard ticks radius="xl">
            <WorldDotMap pins={mapQuery.data?.pins ?? []} you={null} />
          </MapCard>
        </Dimmed>
        <Scrim />
        <SheetDock>
          <GeoConsentSheet
            detected={status.data.detected}
            pending={consent.isPending}
            errorText={consent.error ? displayError(consent.error) : null}
            onConsent={() => consent.mutate()}
            onNotNow={() => navigate("/board")}
          />
        </SheetDock>
      </Stack>
    );
  }

  if (mapQuery.isLoading) return <Loading label="mapping raccoons…" />;
  if (mapQuery.error) return <ErrorNotice message={displayError(mapQuery.error)} />;
  if (!mapQuery.data) return null;

  return (
    <MapView
      map={mapQuery.data}
      pinned={status.data.pinned}
      onHide={() => hide.mutate()}
      hidePending={hide.isPending}
    />
  );
}
